import { useState, useEffect, useRef } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { DataTable } from '../../components/data-table/DataTable.js';
import { Pagination } from '../../components/data-table/Pagination.js';
import { TableToolbar } from '../../components/data-table/TableToolbar.js';
import { Popover } from '../../components/ui/Popover.js';
import { SkeletonCard } from '../../components/ui/Skeleton.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useAreas } from '../../hooks/useAreas.js';
import { buildingsApi } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import type { BuildingListItem } from '@imza/shared';

export const Route = createFileRoute('/_auth/buildings')({
  component: BuildingsPage,
});

type SortKey = 'name' | 'units_desc';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

function BuildingsPage() {
  const [q, setQ] = useState('');
  const [selectedAreaIds, setSelectedAreaIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<SortKey>('name');
  const [showAreaFilter, setShowAreaFilter] = useState(false);
  const areaFilterRef = useRef<HTMLButtonElement>(null);

  const debouncedQ = useDebounce(q, 250);

  // Area filter is single-select by API — we pass the first selected area_id
  // (multiselect UI but API takes one area_id; future: extend API)
  const areaId = selectedAreaIds.length === 1 ? selectedAreaIds[0] : undefined;

  // Reset page when filters/sort change
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, areaId, sort, pageSize]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['buildings-list', { q: debouncedQ, areaId, page, pageSize, sort }],
    queryFn: () =>
      buildingsApi.listPaginated({
        q: debouncedQ || undefined,
        area_id: areaId,
        page,
        pageSize,
        sort,
      }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const { data: areas } = useAreas();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  function handleSort(key: string) {
    if (key === 'name') {
      setSort('name');
    } else if (key === 'units') {
      setSort((prev) => (prev === 'units_desc' ? 'name' : 'units_desc'));
    }
  }

  function toggleArea(id: number) {
    setSelectedAreaIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  }

  const buildingLabel = total === 1 ? '1 building' : `${total.toLocaleString()} buildings`;

  // Sort direction for header arrows
  const sortBy = sort === 'name' ? 'name' : 'units';
  const sortDir = sort === 'units_desc' ? 'desc' : 'asc';

  return (
    <div className="flex flex-col h-full">
      {/* Page header — desktop only */}
      <div className="hidden sm:flex items-center justify-between px-6 py-4 border-b border-border bg-white">
        <div>
          <h1 className="text-xl font-bold text-primary-text">Buildings</h1>
          {!isLoading && (
            <p className="text-xs text-secondary mt-0.5">
              {isFetching && !isLoading
                ? 'Updating…'
                : <><span className="font-semibold text-primary-text">{buildingLabel}</span></>
              }
            </p>
          )}
        </div>
      </div>

      {/* ── MOBILE LAYOUT ─────────────────────────────────────────────── */}
      <div className="sm:hidden bg-white border-b border-border px-4 pt-4 pb-3 space-y-2">
        {/* Search bar */}
        <div className="relative flex items-center">
          <Search className="absolute left-3.5 w-5 h-5 text-secondary pointer-events-none" aria-hidden="true" />
          <input
            type="search"
            placeholder="Search buildings…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={cn(
              'w-full h-12 pl-10 pr-10 rounded-xl border text-sm',
              'border-border bg-white text-primary-text placeholder:text-secondary',
              'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
              'transition-shadow'
            )}
            aria-label="Search buildings"
          />
          {q && (
            <button
              onClick={() => setQ('')}
              className="absolute right-3 p-1 rounded-full text-secondary hover:text-primary-text transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile: count */}
      <div className="sm:hidden bg-white border-b border-border px-4 py-1.5">
        <p className="text-xs text-secondary">
          {isFetching && !isLoading ? 'Updating…' : (
            <span className="font-semibold text-primary-text">{buildingLabel}</span>
          )}
        </p>
      </div>

      {/* Mobile: card list */}
      <div className="sm:hidden flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <Search className="w-10 h-10 text-secondary mb-3" />
            <p className="font-semibold text-primary-text">No buildings match your filters</p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {items.map((building, idx) => (
              <BuildingMobileCard
                key={building.name}
                building={building}
                rowNum={(page - 1) * pageSize + idx + 1}
              />
            ))}
          </div>
        )}
        {totalPages > 1 && (
          <div className="bg-white border-t border-border px-4 py-3 flex items-center justify-between gap-3">
            <button
              className={cn(
                'px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors',
                page <= 1
                  ? 'border-border text-secondary opacity-40 cursor-not-allowed'
                  : 'border-border text-secondary hover:border-primary hover:text-primary-text'
              )}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <p className="text-xs text-secondary">Page {page} of {totalPages}</p>
            <button
              className={cn(
                'px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors',
                page >= totalPages
                  ? 'border-border text-secondary opacity-40 cursor-not-allowed'
                  : 'border-border text-secondary hover:border-primary hover:text-primary-text'
              )}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* ── DESKTOP LAYOUT ──────────────────────────────────────────────── */}
      <div className="hidden sm:flex flex-col flex-1 overflow-hidden">
        <TableToolbar
          left={
            <>
              {/* Search input */}
              <div className="relative flex items-center w-[300px] lg:w-[420px] xl:w-[480px]">
                <Search className="absolute left-3 w-4 h-4 text-secondary pointer-events-none" aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Search buildings…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className={cn(
                    'w-full h-10 pl-9 pr-8 rounded-lg border text-sm',
                    'border-border bg-white text-primary-text placeholder:text-secondary',
                    'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
                    'transition-shadow'
                  )}
                  aria-label="Search buildings"
                />
                {q && (
                  <button
                    onClick={() => setQ('')}
                    className="absolute right-2.5 p-0.5 rounded-full text-secondary hover:text-primary-text transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </>
          }
          right={
            <>
              {/* Row count selector */}
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="border border-border rounded-lg px-2 py-1.5 text-xs text-secondary min-h-[36px] focus:outline-none focus:border-primary"
                aria-label="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>Show {n}</option>
                ))}
              </select>

              {/* Area filter */}
              <div className="relative">
                <button
                  ref={areaFilterRef}
                  onClick={() => setShowAreaFilter((v) => !v)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors min-h-[36px]',
                    (showAreaFilter || selectedAreaIds.length > 0)
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-border text-secondary hover:border-primary hover:text-primary-text'
                  )}
                  aria-label={`Area filter${selectedAreaIds.length > 0 ? ` (${selectedAreaIds.length} selected)` : ''}`}
                  aria-expanded={showAreaFilter}
                  aria-haspopup="true"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Area
                  {selectedAreaIds.length > 0 && (
                    <span className="bg-primary text-white text-[10px] rounded-full px-1.5">{selectedAreaIds.length}</span>
                  )}
                </button>

                <Popover
                  open={showAreaFilter}
                  onClose={() => setShowAreaFilter(false)}
                  triggerRef={areaFilterRef as React.RefObject<HTMLElement>}
                  minWidth={220}
                  maxWidth={280}
                >
                  <div className="p-3 space-y-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-primary-text">Filter by Area</span>
                      {selectedAreaIds.length > 0 && (
                        <button
                          onClick={() => setSelectedAreaIds([])}
                          className="text-[10px] text-primary hover:underline"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {areas?.map((area) => (
                      <label
                        key={area.id}
                        className="flex items-center gap-2 px-1 py-1 rounded hover:bg-hover-bg cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAreaIds.includes(area.id)}
                          onChange={() => toggleArea(area.id)}
                          className="w-3.5 h-3.5 rounded border-border accent-primary"
                        />
                        <span className="text-xs text-primary-text">{area.name}</span>
                      </label>
                    ))}
                  </div>
                </Popover>
              </div>
            </>
          }
        />

        {/* Table */}
        <div className="flex-1 overflow-auto relative z-0">
          <DataTable
            columns={[
              { key: '#', label: '#', className: 'w-12 text-right' },
              { key: 'name', label: 'Building', sortable: true },
              { key: 'area', label: 'Area' },
              { key: 'units', label: 'Units', sortable: true, className: 'text-right' },
            ]}
            rows={items}
            rowKey={(r) => r.name}
            isLoading={isLoading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            emptyState={
              <div className="text-center py-4">
                <Search className="w-8 h-8 text-secondary mx-auto mb-2" />
                <p className="font-semibold text-primary-text">No buildings match your filters</p>
                <p className="text-xs text-secondary mt-1">Try adjusting your search or area filter</p>
              </div>
            }
            renderRow={(building, _visibleColumns) => {
              const idx = items.findIndex((b) => b.name === building.name);
              return (
                <BuildingTableRow
                  key={building.name}
                  building={building}
                  rowNum={(page - 1) * pageSize + idx + 1}
                />
              );
            }}
          />
        </div>

        {/* Pagination */}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}

function ChipLink({
  to,
  search,
  children,
}: {
  to: string;
  search: Record<string, string | number | undefined>;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      search={search as never}
      className="inline-block bg-primary/10 text-primary rounded-md px-2 py-0.5 text-xs font-medium hover:bg-primary/20 transition-colors max-w-[200px] truncate"
    >
      {children}
    </Link>
  );
}

function BuildingTableRow({
  building,
  rowNum,
}: {
  building: BuildingListItem;
  rowNum: number;
}) {
  return (
    <>
      <td className="px-4 py-3 w-12 text-right text-xs text-secondary tabular-nums">
        {rowNum}
      </td>
      <td className="px-4 py-3">
        <ChipLink to="/landlords" search={{ building: building.name }}>
          {building.name}
        </ChipLink>
      </td>
      <td className="px-4 py-3">
        {building.areaName && building.areaId != null ? (
          <ChipLink to="/landlords" search={{ area_id: building.areaId }}>
            {building.areaName}
          </ChipLink>
        ) : (
          <span className="text-secondary text-xs">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <span className="font-semibold text-primary-text tabular-nums text-sm">
          {building.unitCount.toLocaleString()}
        </span>
      </td>
      {/* Empty actions placeholder to match DataTable's extra th */}
      <td className="px-3 py-3 w-20" />
    </>
  );
}

function BuildingMobileCard({
  building,
  rowNum,
}: {
  building: BuildingListItem;
  rowNum: number;
}) {
  return (
    <div className="bg-white border border-border rounded-xl px-4 py-3 flex items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <span className="text-xs text-secondary tabular-nums mt-0.5 flex-shrink-0 w-5 text-right">
          {rowNum}
        </span>
        <div className="min-w-0">
          <Link
            to="/landlords"
            search={{ building: building.name } as never}
            className="inline-block bg-primary/10 text-primary rounded-md px-2 py-0.5 text-xs font-medium hover:bg-primary/20 transition-colors max-w-full truncate"
          >
            {building.name}
          </Link>
          {building.areaName && building.areaId != null && (
            <div className="mt-1">
              <Link
                to="/landlords"
                search={{ area_id: building.areaId } as never}
                className="inline-block bg-primary/10 text-primary rounded-md px-2 py-0.5 text-xs font-medium hover:bg-primary/20 transition-colors max-w-full truncate"
              >
                {building.areaName}
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <span className="font-semibold text-primary-text tabular-nums text-sm">
          {building.unitCount.toLocaleString()}
        </span>
        <p className="text-[10px] text-secondary">units</p>
      </div>
    </div>
  );
}

